import { useEffect, useRef, useState, type ReactNode } from "react";
import styles from "./ds.module.css";
import { SearchField } from "./fields";

/*
    Filter chips with popovers, the shape every list toolbar uses: the parent owns the filter
    values; a chip owns only whether its popover is open and its search text. Applied filters
    turn the chip indigo (state, not decoration) and are listed again as removable chips.
*/

/** A chip that opens a popover beneath it; closes on an outside click or Escape. */
export function ChipPopover({ label, active = false, count, icon, open, onOpenChange, popWidth = 280, alignRight = false, children, title }: {
    label: ReactNode;
    active?: boolean;
    count?: number | null;
    icon?: ReactNode;
    open: boolean;
    onOpenChange: (open: boolean) => void;
    popWidth?: number | string;
    alignRight?: boolean;
    children: ReactNode;
    /** The uppercase heading inside the popover. */
    title?: ReactNode;
}) {
    const root = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            if (root.current && !root.current.contains(e.target as Node)) onOpenChange(false);
        };
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onOpenChange(false); };
        document.addEventListener("mousedown", onDoc);
        document.addEventListener("keydown", onKey);
        return () => {
            document.removeEventListener("mousedown", onDoc);
            document.removeEventListener("keydown", onKey);
        };
    }, [open, onOpenChange]);

    return (
        <div className={styles.chipWrap} ref={root}>
            <button type="button" className={`${styles.chip} ${active ? styles.chipOn : ""}`} aria-expanded={open} aria-haspopup="dialog"
                onClick={() => onOpenChange(!open)}>
                {icon}
                <span>{label}</span>
                {count != null && count > 1 && <span className={styles.chipCount}>{count}</span>}
                <span aria-hidden="true" style={{ fontSize: 10 }}>▾</span>
            </button>
            {open && (
                <div className={`${styles.pop} ${alignRight ? styles.popRight : ""}`} style={{ width: popWidth }} role="dialog">
                    {title && <div className={styles.popLabel}>{title}</div>}
                    {children}
                </div>
            )}
        </div>
    );
}

export interface FilterOption { value: string; label: string; hint?: string }

interface Common {
    label: string;
    options: FilterOption[];
    searchable?: boolean;
    placeholder?: string;
    popWidth?: number | string;
    icon?: ReactNode;
    /** Extra controls above the list: a Match Any / Match All switch, say. */
    header?: ReactNode;
}

export type FilterMenuProps = Common & (
    | { kind: "multi"; value: string[]; onChange: (value: string[]) => void }
    | { kind: "single"; value: string; onChange: (value: string) => void; /** The value that reads as "no filter"; the chip stays plain on it. */ allValue?: string }
);

/** One filter chip: a multi-select with checkboxes or a single-select with a check mark. */
export function FilterMenu(props: FilterMenuProps) {
    const { label, options, searchable = false, placeholder, popWidth, icon, header } = props;
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState("");

    const multi = props.kind === "multi";
    const selected = new Set(multi ? props.value : [props.value]);
    const chosen = options.filter((o) => selected.has(o.value));
    const active = multi ? chosen.length > 0 : props.value !== (props.allValue ?? "") && props.value !== "";
    const chipLabel = !active ? label
        : multi ? (chosen.length === 1 ? `${label}: ${chosen[0].label}` : label)
            : `${label}: ${chosen[0]?.label ?? props.value}`;

    const q = query.trim().toLowerCase();
    const shown = options.filter((o) => !q || o.label.toLowerCase().includes(q));

    const pick = (o: FilterOption) => {
        if (props.kind === "multi") {
            const next = selected.has(o.value) ? props.value.filter((v) => v !== o.value) : [...props.value, o.value];
            props.onChange(next);
        } else {
            props.onChange(o.value);
            setOpen(false);
        }
    };

    return (
        <ChipPopover label={chipLabel} active={active} count={multi ? chosen.length : null} icon={icon}
            open={open} onOpenChange={setOpen} popWidth={popWidth} title={label}>
            {header && <div className={styles.popSearch}>{header}</div>}
            {searchable && (
                <div className={styles.popSearch}>
                    <SearchField value={query} onChange={setQuery} placeholder={placeholder ?? "Search…"} width="100%" label={`Search ${label}`} />
                </div>
            )}
            <div className={styles.popList} role={multi ? "group" : "listbox"} aria-label={label}>
                {shown.map((o) => (
                    <button key={o.value} type="button" className={styles.popOption} onClick={() => pick(o)}
                        role={multi ? "checkbox" : "option"} aria-checked={multi ? selected.has(o.value) : undefined}
                        aria-selected={multi ? undefined : selected.has(o.value)}>
                        <span className={styles.popOptionLabel}>
                            {multi && <span className={`${styles.checkbox} ${selected.has(o.value) ? styles.checkboxOn : ""}`} aria-hidden="true">{selected.has(o.value) ? "✓" : ""}</span>}
                            <span>{o.label}</span>
                        </span>
                        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            {o.hint && <span className={styles.hint}>{o.hint}</span>}
                            {!multi && selected.has(o.value) && <i className={`bi bi-check2 ${styles.check}`} aria-hidden="true" />}
                        </span>
                    </button>
                ))}
                {shown.length === 0 && <div className={styles.popNone}>No matches</div>}
            </div>
            {multi && (
                <div className={styles.popFoot}>
                    <span>{chosen.length ? `${chosen.length} selected` : "None selected"}</span>
                    <button type="button" className={styles.btnLink} onClick={() => props.onChange([])}>Clear</button>
                </div>
            )}
        </ChipPopover>
    );
}

export function FilterBar({ children, hint, style }: { children: ReactNode; hint?: ReactNode; style?: React.CSSProperties }) {
    return (
        <div className={styles.filterBar} style={style}>
            {children}
            {hint && <span className={styles.filterHint}><i className="bi bi-info-circle" aria-hidden="true" /><span>{hint}</span></span>}
        </div>
    );
}

export function FilterDivider() {
    return <span className={styles.divider} aria-hidden="true" />;
}

export interface AppliedChip { label: string; remove: () => void }

/** The applied filters, each removable, with "Clear all". Renders nothing when none apply. */
export function ActiveFilterChips({ chips, onClearAll, leadLabel, edge = "bottom" }: {
    chips: AppliedChip[]; onClearAll: () => void; leadLabel?: ReactNode; edge?: "top" | "bottom";
}) {
    if (!chips.length) return null;
    return (
        <div className={`${styles.applied} ${edge === "top" ? styles.appliedTop : styles.appliedBottom}`}>
            {leadLabel && <span className={styles.appliedLead}>{leadLabel}</span>}
            {chips.map((c, i) => (
                <span key={i} className={styles.appliedChip}>
                    <span>{c.label}</span>
                    <button type="button" className={styles.appliedRemove} aria-label={`Remove filter ${c.label}`} onClick={c.remove}>×</button>
                </span>
            ))}
            <button type="button" className={styles.btnLink} style={{ padding: "0 4px" }} onClick={onClearAll}>Clear all</button>
        </div>
    );
}
